import { useEffect, useRef, useState } from "react";

import {
  API, btn, C, DobSelect, dobIso, dobReady, fill, FONT, Header, label, nameIn, openWhatsApp,
  PLAY_URL, sel, shareNodeAsImage, SITE, usePublicLang,
} from "./publicShared";

// Free, no-login "Vedic Archetype" quiz — the shareable entry point.
// Opened with /?page=archetype (from reels, WhatsApp shares, bio links).
// /?page=archetype&a=<rashi_idx> means a friend shared their result.
// Nothing is stored; the backend only calculates the Moon sign.

// Archetype names only (generated from src/jyotish.py ARCHETYPES) — used for the "your friend is ___" banner before any
// API call. Order = Moon rashi index (Mesha … Meena), same as src/jyotish.py.
const FRIEND = [
  {"en": "Hanuman", "hi": "हनुमान", "te": "హనుమాన్", "ta": "அனுமன்", "kn": "ಹನುಮಂತ", "mr": "हनुमान", "emoji": "🐒"},
  {"en": "Yudhishthira", "hi": "युधिष्ठिर", "te": "ధర్మరాజు", "ta": "தர்மர்", "kn": "ಯುಧಿಷ್ಠಿರ", "mr": "युधिष्ठिर", "emoji": "⚖️"},
  {"en": "Arjuna", "hi": "अर्जुन", "te": "అర్జునుడు", "ta": "அர்ஜுனன்", "kn": "ಅರ್ಜುನ", "mr": "अर्जुन", "emoji": "🏹"},
  {"en": "Rama", "hi": "श्री राम", "te": "శ్రీరాముడు", "ta": "இராமன்", "kn": "ಶ್ರೀ ರಾಮ", "mr": "श्री राम", "emoji": "🌙"},
  {"en": "Lakshmana", "hi": "लक्ष्मण", "te": "లక్ష్మణుడు", "ta": "இலக்குவன்", "kn": "ಲಕ್ಷ್ಮಣ", "mr": "लक्ष्मण", "emoji": "🦁"},
  {"en": "Sita", "hi": "सीता", "te": "సీతాదేవి", "ta": "சீதை", "kn": "ಸೀತಾ", "mr": "सीता", "emoji": "🌸"},
  {"en": "Krishna", "hi": "श्री कृष्ण", "te": "శ్రీకృష్ణుడు", "ta": "கண்ணன்", "kn": "ಶ್ರೀ ಕೃಷ್ಣ", "mr": "श्री कृष्ण", "emoji": "🪈"},
  {"en": "Karna", "hi": "कर्ण", "te": "కర్ణుడు", "ta": "கர்ணன்", "kn": "ಕರ್ಣ", "mr": "कर्ण", "emoji": "☀️"},
  {"en": "Drona", "hi": "द्रोणाचार्य", "te": "ద్రోణాచార్య", "ta": "துரோணர்", "kn": "ದ್ರೋಣಾಚಾರ್ಯ", "mr": "द्रोणाचार्य", "emoji": "🎯"},
  {"en": "Bhishma", "hi": "भीष्म", "te": "భీష్ముడు", "ta": "பீஷ்மர்", "kn": "ಭೀಷ್ಮ", "mr": "भीष्म", "emoji": "🏔️"},
  {"en": "Vidura", "hi": "विदुर", "te": "విదురుడు", "ta": "விதுரர்", "kn": "ವಿದುರ", "mr": "विदुर", "emoji": "🔮"},
  {"en": "Kunti", "hi": "कुंती", "te": "కుంతీదేవి", "ta": "குந்தி", "kn": "ಕುಂತಿ", "mr": "कुंती", "emoji": "🌊"},
];

const T = {
  en: {
    title: "Discover your Vedic Archetype",
    sub: "Which great soul from the Ramayana and Mahabharata lives in you? Calculated from your Moon sign at birth. Free, no sign-up.",
    friend: "Your friend is {n} {e}. Which one are you?",
    dob: "Your date of birth",
    time: "Time of birth (if you know it)", timeHelp: "Not sure? Leave it empty — we'll tell you if it matters.",
    go: "Show my archetype", wait: "Reading the stars…", err: "Something went wrong. Please try again.",
    iam: "I am", moon: "Moon sign", nak: "Birth star",
    dharma: "Your dharma", gita: "From the Gita",
    unsure: "On your birth day the Moon changed sign. You are {a} or {b}, depending on the time you were born. Add your birth time to be sure.",
    shareWa: "Share on WhatsApp", shareImg: "Share as image", copy: "Copy link", copied: "Link copied ✓",
    again: "Try another date",
    fullTitle: "This is only the beginning",
    fullText: "Your full free reading shows your Kundali chart, current Dasha period, doshas and what they mean for your life.",
    fullBtn: "See my full reading — free",
    app: "Get the Android app",
    shareMsg: "🔱 My Vedic Archetype is {n} {e}\n\n“{d}”\n\nWhich great soul from the Ramayana and Mahabharata lives in you? Find out free (10 seconds):\n{u}",
  },
  hi: {
    title: "जानिए आपका वैदिक आर्किटाइप",
    sub: "रामायण और महाभारत की कौन-सी महान आत्मा आपमें बसती है? जन्म के समय आपकी चंद्र राशि से। मुफ़्त, बिना साइन-अप।",
    friend: "आपके मित्र {n} {e} हैं। आप कौन हैं?",
    dob: "आपकी जन्म तिथि",
    time: "जन्म का समय (यदि पता हो)", timeHelp: "पता नहीं? खाली छोड़ दें — ज़रूरत हुई तो हम बताएँगे।",
    go: "मेरा आर्किटाइप दिखाएँ", wait: "तारों को पढ़ रहे हैं…", err: "कुछ गड़बड़ हुई। कृपया फिर से कोशिश करें।",
    iam: "मैं हूँ", moon: "चंद्र राशि", nak: "जन्म नक्षत्र",
    dharma: "आपका धर्म", gita: "गीता से",
    unsure: "आपके जन्मदिन पर चंद्रमा ने राशि बदली। जन्म के समय के अनुसार आप {a} या {b} हैं। पक्का जानने के लिए जन्म समय जोड़ें।",
    shareWa: "WhatsApp पर भेजें", shareImg: "फ़ोटो के रूप में भेजें", copy: "लिंक कॉपी करें", copied: "लिंक कॉपी हो गया ✓",
    again: "दूसरी तिथि देखें",
    fullTitle: "यह तो बस शुरुआत है",
    fullText: "आपकी पूरी मुफ़्त रीडिंग में कुंडली, अभी चल रही दशा, दोष और आपके जीवन पर उनका असर है।",
    fullBtn: "मेरी पूरी रीडिंग देखें — मुफ़्त",
    app: "Android ऐप पाएँ",
    shareMsg: "🔱 मेरा वैदिक आर्किटाइप है {n} {e}\n\n“{d}”\n\nरामायण-महाभारत की कौन-सी महान आत्मा आपमें है? मुफ़्त जानें (10 सेकंड):\n{u}",
  },
  te: {
    title: "మీ వైదిక ఆర్కిటైప్ తెలుసుకోండి",
    sub: "రామాయణ, మహాభారతాల్లోని ఏ మహాత్ముడు మీలో ఉన్నారు? మీ జన్మ చంద్ర రాశి ఆధారంగా. ఉచితం, సైన్-అప్ అవసరం లేదు.",
    friend: "మీ స్నేహితుడు {n} {e}. మీరు ఎవరు?",
    dob: "మీ పుట్టిన తేదీ",
    time: "పుట్టిన సమయం (తెలిస్తే)", timeHelp: "తెలియదా? ఖాళీగా వదిలేయండి — అవసరమైతే మేమే చెబుతాం.",
    go: "నా ఆర్కిటైప్ చూపించు", wait: "నక్షత్రాలను చదువుతున్నాం…", err: "ఏదో తప్పు జరిగింది. మళ్లీ ప్రయత్నించండి.",
    iam: "నేను", moon: "చంద్ర రాశి", nak: "జన్మ నక్షత్రం",
    dharma: "మీ ధర్మం", gita: "గీత నుండి",
    unsure: "మీ పుట్టిన రోజున చంద్రుడు రాశి మారాడు. పుట్టిన సమయాన్ని బట్టి మీరు {a} లేదా {b}. ఖచ్చితంగా తెలియడానికి పుట్టిన సమయం జోడించండి.",
    shareWa: "WhatsApp లో పంపండి", shareImg: "ఫోటోగా పంపండి", copy: "లింక్ కాపీ చేయండి", copied: "లింక్ కాపీ అయింది ✓",
    again: "మరో తేదీ చూడండి",
    fullTitle: "ఇది మొదలు మాత్రమే",
    fullText: "మీ పూర్తి ఉచిత రీడింగ్‌లో జాతక చక్రం, ప్రస్తుత దశ, దోషాలు, అవి మీ జీవితంపై చూపే ప్రభావం ఉంటాయి.",
    fullBtn: "నా పూర్తి రీడింగ్ చూడండి — ఉచితం",
    app: "Android యాప్ పొందండి",
    shareMsg: "🔱 నా వైదిక ఆర్కిటైప్ {n} {e}\n\n“{d}”\n\nరామాయణ-మహాభారతాల్లోని ఏ మహాత్ముడు మీలో ఉన్నారు? ఉచితంగా తెలుసుకోండి (10 సెకన్లు):\n{u}",
  },
  ta: {
    title: "உங்கள் வேத ஆர்க்கிடைப்பை அறியுங்கள்",
    sub: "ராமாயணம், மகாபாரதத்தின் எந்த மகான் உங்களுக்குள் வாழ்கிறார்? உங்கள் ஜென்ம சந்திர ராசியிலிருந்து. இலவசம், பதிவு தேவையில்லை.",
    friend: "உங்கள் நண்பர் {n} {e}. நீங்கள் யார்?",
    dob: "உங்கள் பிறந்த தேதி",
    time: "பிறந்த நேரம் (தெரிந்தால்)", timeHelp: "தெரியவில்லையா? காலியாக விடுங்கள் — தேவைப்பட்டால் சொல்வோம்.",
    go: "என் ஆர்க்கிடைப்பைக் காட்டு", wait: "நட்சத்திரங்களைப் படிக்கிறோம்…", err: "ஏதோ தவறு. மீண்டும் முயற்சிக்கவும்.",
    iam: "நான்", moon: "சந்திர ராசி", nak: "ஜென்ம நட்சத்திரம்",
    dharma: "உங்கள் தர்மம்", gita: "கீதையிலிருந்து",
    unsure: "நீங்கள் பிறந்த நாளில் சந்திரன் ராசி மாறினார். பிறந்த நேரத்தைப் பொறுத்து நீங்கள் {a} அல்லது {b}. உறுதியாக அறிய பிறந்த நேரத்தைச் சேர்க்கவும்.",
    shareWa: "WhatsApp-ல் பகிருங்கள்", shareImg: "படமாகப் பகிருங்கள்", copy: "இணைப்பை நகலெடு", copied: "இணைப்பு நகலெடுக்கப்பட்டது ✓",
    again: "வேறு தேதி பாருங்கள்",
    fullTitle: "இது ஆரம்பம் மட்டுமே",
    fullText: "உங்கள் முழு இலவச வாசிப்பில் ஜாதகம், தற்போதைய தசை, தோஷங்கள், அவை உங்கள் வாழ்வில் ஏற்படுத்தும் தாக்கம் உள்ளன.",
    fullBtn: "என் முழு வாசிப்பைப் பார் — இலவசம்",
    app: "Android செயலியைப் பெறுங்கள்",
    shareMsg: "🔱 என் வேத ஆர்க்கிடைப் {n} {e}\n\n“{d}”\n\nராமாயணம்-மகாபாரதத்தின் எந்த மகான் உங்களுக்குள்? இலவசமாக அறியுங்கள் (10 வினாடி):\n{u}",
  },
  kn: {
    title: "ನಿಮ್ಮ ವೈದಿಕ ಆರ್ಕಿಟೈಪ್ ತಿಳಿಯಿರಿ",
    sub: "ರಾಮಾಯಣ, ಮಹಾಭಾರತದ ಯಾವ ಮಹಾತ್ಮರು ನಿಮ್ಮಲ್ಲಿದ್ದಾರೆ? ನಿಮ್ಮ ಜನ್ಮ ಚಂದ್ರ ರಾಶಿಯಿಂದ. ಉಚಿತ, ಸೈನ್-ಅಪ್ ಬೇಡ.",
    friend: "ನಿಮ್ಮ ಸ್ನೇಹಿತರು {n} {e}. ನೀವು ಯಾರು?",
    dob: "ನಿಮ್ಮ ಜನ್ಮ ದಿನಾಂಕ",
    time: "ಜನ್ಮ ಸಮಯ (ಗೊತ್ತಿದ್ದರೆ)", timeHelp: "ಗೊತ್ತಿಲ್ಲವೇ? ಖಾಲಿ ಬಿಡಿ — ಅಗತ್ಯವಿದ್ದರೆ ನಾವು ಹೇಳುತ್ತೇವೆ.",
    go: "ನನ್ನ ಆರ್ಕಿಟೈಪ್ ತೋರಿಸಿ", wait: "ನಕ್ಷತ್ರಗಳನ್ನು ಓದುತ್ತಿದ್ದೇವೆ…", err: "ಏನೋ ತಪ್ಪಾಯಿತು. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.",
    iam: "ನಾನು", moon: "ಚಂದ್ರ ರಾಶಿ", nak: "ಜನ್ಮ ನಕ್ಷತ್ರ",
    dharma: "ನಿಮ್ಮ ಧರ್ಮ", gita: "ಗೀತೆಯಿಂದ",
    unsure: "ನಿಮ್ಮ ಜನ್ಮದಿನದಂದು ಚಂದ್ರ ರಾಶಿ ಬದಲಿಸಿದ. ಜನ್ಮ ಸಮಯದ ಪ್ರಕಾರ ನೀವು {a} ಅಥವಾ {b}. ಖಚಿತವಾಗಿ ತಿಳಿಯಲು ಜನ್ಮ ಸಮಯ ಸೇರಿಸಿ.",
    shareWa: "WhatsApp ನಲ್ಲಿ ಹಂಚಿ", shareImg: "ಚಿತ್ರವಾಗಿ ಹಂಚಿ", copy: "ಲಿಂಕ್ ನಕಲಿಸಿ", copied: "ಲಿಂಕ್ ನಕಲಾಯಿತು ✓",
    again: "ಬೇರೆ ದಿನಾಂಕ ನೋಡಿ",
    fullTitle: "ಇದು ಆರಂಭ ಮಾತ್ರ",
    fullText: "ನಿಮ್ಮ ಪೂರ್ಣ ಉಚಿತ ರೀಡಿಂಗ್‌ನಲ್ಲಿ ಜಾತಕ, ಈಗಿನ ದಶೆ, ದೋಷಗಳು ಮತ್ತು ಅವು ನಿಮ್ಮ ಜೀವನದ ಮೇಲೆ ಬೀರುವ ಪ್ರಭಾವ ಇದೆ.",
    fullBtn: "ನನ್ನ ಪೂರ್ಣ ರೀಡಿಂಗ್ ನೋಡಿ — ಉಚಿತ",
    app: "Android ಆ್ಯಪ್ ಪಡೆಯಿರಿ",
    shareMsg: "🔱 ನನ್ನ ವೈದಿಕ ಆರ್ಕಿಟೈಪ್ {n} {e}\n\n“{d}”\n\nರಾಮಾಯಣ-ಮಹಾಭಾರತದ ಯಾವ ಮಹಾತ್ಮರು ನಿಮ್ಮಲ್ಲಿ? ಉಚಿತವಾಗಿ ತಿಳಿಯಿರಿ (10 ಸೆಕೆಂಡ್):\n{u}",
  },
  mr: {
    title: "तुमचा वैदिक आर्किटाइप जाणून घ्या",
    sub: "रामायण आणि महाभारतातील कोणता महात्मा तुमच्यात आहे? तुमच्या जन्म चंद्र राशीवरून. मोफत, साइन-अप नाही.",
    friend: "तुमचा मित्र {n} {e} आहे. तुम्ही कोण?",
    dob: "तुमची जन्मतारीख",
    time: "जन्माची वेळ (माहीत असल्यास)", timeHelp: "माहीत नाही? रिकामे ठेवा — गरज असेल तर आम्ही सांगू.",
    go: "माझा आर्किटाइप दाखवा", wait: "ताऱ्यांचे वाचन चालू आहे…", err: "काहीतरी चुकले. पुन्हा प्रयत्न करा.",
    iam: "मी आहे", moon: "चंद्र रास", nak: "जन्म नक्षत्र",
    dharma: "तुमचा धर्म", gita: "गीतेतून",
    unsure: "तुमच्या जन्मदिवशी चंद्राने रास बदलली. जन्मवेळेनुसार तुम्ही {a} किंवा {b} आहात. खात्रीसाठी जन्मवेळ जोडा.",
    shareWa: "WhatsApp वर पाठवा", shareImg: "फोटो म्हणून पाठवा", copy: "लिंक कॉपी करा", copied: "लिंक कॉपी झाली ✓",
    again: "दुसरी तारीख पहा",
    fullTitle: "ही तर फक्त सुरुवात आहे",
    fullText: "तुमच्या पूर्ण मोफत वाचनात कुंडली, सध्याची दशा, दोष आणि त्यांचा तुमच्या जीवनावरील परिणाम आहे.",
    fullBtn: "माझे पूर्ण वाचन पहा — मोफत",
    app: "Android ॲप मिळवा",
    shareMsg: "🔱 माझा वैदिक आर्किटाइप आहे {n} {e}\n\n“{d}”\n\nरामायण-महाभारतातील कोणता महात्मा तुमच्यात आहे? मोफत जाणून घ्या (10 सेकंद):\n{u}",
  },
};

export default function ArchetypeQuizPage({ onFullReading }) {
  const params = new URLSearchParams(window.location.search);
  const friendIdx = Number.isInteger(Number(params.get("a"))) && params.get("a") !== null ? Number(params.get("a")) : -1;
  const friend = FRIEND[friendIdx];

  const [lang, setLang] = usePublicLang(T);
  const t = T[lang];
  const [dob, setDob] = useState({ d: "", m: "", y: "" });
  const [time, setTime] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const resultRef = useRef(null);

  const ready = dobReady(dob);

  async function fetchArchetype(withTime) {
    if (!ready) return;
    setLoading(true); setErr(false);
    const q = new URLSearchParams({ dob: dobIso(dob), lang });
    if (withTime) q.set("time", withTime);
    try {
      const r = await fetch(`${API}/public/archetype?${q}`);
      if (!r.ok) throw new Error();
      setRes(await r.json());
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch { setErr(true); }
    setLoading(false);
  }

  // Re-fetch in the new language so desc/traits follow the picker.
  useEffect(() => {
    if (res) fetchArchetype(time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const a = res?.archetype;
  const arcName = a ? nameIn(a, lang) : "";
  const shareUrl = res ? `${SITE}/?page=archetype&a=${res.rashi_idx}` : SITE;
  const shareText = a ? fill(t.shareMsg, { n: arcName, e: a.emoji, d: a.dharma, u: shareUrl }) : "";

  const shareWhatsApp = () => openWhatsApp(shareText);
  const shareImage = () => shareNodeAsImage(cardRef.current, `vedsanga-${a.en.toLowerCase()}.png`, shareText);

  function copyLink() {
    navigator.clipboard?.writeText(shareText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, color: C.text }}>
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "20px 16px 48px" }}>

        <Header lang={lang} setLang={setLang} />

        {friend && !res && (
          <div style={{ background: C.warm, border: `1.5px solid ${C.gold}`, borderRadius: 14, padding: "14px 16px", marginBottom: 18, fontSize: 17, fontWeight: 600, lineHeight: 1.5 }}>
            {fill(t.friend, { n: nameIn(friend, lang), e: friend.emoji })}
          </div>
        )}

        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px, 8vw, 40px)", lineHeight: 1.15, fontWeight: 700, margin: "0 0 12px" }}>{t.title}</h1>
        <p style={{ fontSize: 17, lineHeight: 1.6, color: C.dim, margin: "0 0 24px" }}>{t.sub}</p>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, display: "grid", gap: 18, boxShadow: "0 2px 10px rgba(120,80,20,0.06)" }}>
          <div>
            <span style={label}>{t.dob}</span>
            <DobSelect lang={lang} value={dob} onChange={setDob} />
          </div>
          <div>
            <label htmlFor="arc-time" style={label}>{t.time}</label>
            <input id="arc-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 15, color: C.dim, marginTop: 6, lineHeight: 1.5 }}>{t.timeHelp}</div>
          </div>
          <button onClick={() => fetchArchetype(time)} disabled={!ready || loading} style={{ ...btn(C.accent), opacity: !ready || loading ? 0.55 : 1 }}>
            {loading ? t.wait : t.go}
          </button>
          {err && <div style={{ fontSize: 16, color: "#B42318" }}>{t.err}</div>}
        </div>

        {a && (
          <div ref={resultRef} style={{ marginTop: 28, display: "grid", gap: 16 }}>
            {/* The share card — captured as an image */}
            <div ref={cardRef} style={{ background: C.warm, border: `2px solid ${C.gold}`, borderRadius: 20, padding: "26px 22px", textAlign: "center" }}>
              <div style={{ fontSize: 56, lineHeight: 1 }}>{a.emoji}</div>
              <div style={{ fontSize: 17, color: C.dim, marginTop: 10 }}>{t.iam}</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 40, fontWeight: 700, lineHeight: 1.15, color: C.text }}>{arcName}</div>
              {lang !== "en" && <div style={{ fontSize: 17, color: C.dim }}>{a.en}</div>}
              <div style={{ width: 60, height: 3, background: a.color || C.accent, borderRadius: 2, margin: "14px auto" }} />
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginBottom: 14 }}>
                {(a.traits || []).map((tr) => (
                  <span key={tr} style={{ fontSize: 15, fontWeight: 600, color: C.text, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: "5px 12px" }}>{tr}</span>
                ))}
              </div>
              <div style={{ fontSize: 16, color: C.dim }}>{t.moon}: <b style={{ color: C.text }}>{nameIn(res.rashi, lang)}</b> · {t.nak}: <b style={{ color: C.text }}>{nameIn(res.nakshatra?.name, lang)}</b></div>
              <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.5, marginTop: 14, color: C.text }}>“{a.dharma}”</div>
              <div style={{ fontSize: 15, color: C.accentDark, fontWeight: 700, marginTop: 16 }}>vedsanga.com</div>
            </div>

            {res.time_matters && res.alt_archetype && (
              <div style={{ background: C.card, border: `1.5px solid ${C.accent}`, borderRadius: 14, padding: 16, fontSize: 16, lineHeight: 1.6 }}>
                {fill(t.unsure, { a: `${arcName} ${a.emoji}`, b: `${nameIn(res.alt_archetype, lang)} ${res.alt_archetype.emoji}` })}
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <input aria-label={t.time} type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ ...sel, boxSizing: "border-box" }} />
                  <button onClick={() => fetchArchetype(time)} disabled={!time || loading} style={{ ...btn(C.accent), width: "auto", flex: "0 0 auto", opacity: !time || loading ? 0.55 : 1 }}>{t.go}</button>
                </div>
              </div>
            )}

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, display: "grid", gap: 10 }}>
              <p style={{ fontSize: 17, lineHeight: 1.7, margin: 0 }}>{a.desc}</p>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 6 }}>{t.gita}</div>
              <p style={{ fontSize: 17, lineHeight: 1.6, margin: 0, color: C.dim }}>{a.gita_ref}</p>
            </div>

            <button onClick={shareWhatsApp} style={btn(C.wa)}>{t.shareWa}</button>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={shareImage} style={{ ...btn(C.card, C.text), border: `1.5px solid ${C.border}` }}>{t.shareImg}</button>
              <button onClick={copyLink} style={{ ...btn(C.card, C.text), border: `1.5px solid ${C.border}` }}>{copied ? t.copied : t.copy}</button>
            </div>

            <div style={{ background: C.warm, border: `1.5px solid ${C.gold}`, borderRadius: 16, padding: 18, display: "grid", gap: 12, marginTop: 8 }}>
              <div style={{ fontSize: 21, fontWeight: 800 }}>{t.fullTitle}</div>
              <p style={{ fontSize: 17, lineHeight: 1.6, margin: 0, color: C.dim }}>{t.fullText}</p>
              <button onClick={onFullReading} style={btn(C.accent)}>{t.fullBtn}</button>
              {PLAY_URL && <a href={PLAY_URL} style={{ ...btn(C.card, C.text), border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", boxSizing: "border-box" }}>{t.app}</a>}
            </div>

            <button onClick={() => { setRes(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ ...btn("transparent", C.accentDark), textDecoration: "underline" }}>{t.again}</button>
          </div>
        )}
      </div>
    </div>
  );
}
